// Level 0: Pure parsing functions — no I/O, no side effects
// parseEmployeeHistory extracted from api.js
// parseVDBDescription extracted from LandingPage.js

// Parse employee history string to array of {year, count}
// Format: "2021: 115, 2022: 344, 2023: 303"
export const parseEmployeeHistory = (historyString) => {
  if (!historyString) return [];
  try {
    const entries = historyString.split(',').map(entry => entry.trim());
    return entries.map(entry => {
      const [year, count] = entry.split(':').map(s => s.trim());
      return { year: parseInt(year), count: parseInt(count) };
    }).filter(entry => !isNaN(entry.year) && !isNaN(entry.count));
  } catch {
    return [];
  }
};

/**
 * Parse VDB description text to extract structured fields
 * Pinecone stores data like: "Description text... Industry: X City: Y State: Z Country: W Total Raised: $XXM..."
 * This function extracts those fields into a structured object
 */
export const parseVDBDescription = (text) => {
  if (!text || typeof text !== 'string') {
    return { cleanDescription: '', fields: {}, keywords: [], verticals: [] };
  }

  const fields = {};
  const fieldPatterns = [
    { key: 'industry', pattern: /Industry:\s*([^A-Z][^:]*?)(?=\s+[A-Z][a-z]+:|$)/i },
    { key: 'city', pattern: /City:\s*([^A-Z][^:]*?)(?=\s+[A-Z][a-z]+:|$)/i },
    { key: 'state', pattern: /State:\s*([^A-Z][^:]*?)(?=\s+[A-Z][a-z]+:|$)/i },
    { key: 'country', pattern: /Country:\s*([^A-Z][^:]*?)(?=\s+[A-Z][a-z]+:|$)/i },
    { key: 'location', pattern: /Location:\s*([^A-Z][^:]*?)(?=\s+[A-Z][a-z]+:|$)/i },
    { key: 'totalRaised', pattern: /Total Raised:\s*(\$?[\d,.]+[BMK]?M?)/i },
    { key: 'lastFinancingDate', pattern: /Last Financing Date:\s*([^\s]+(?:\s+[^\s:]+)?)/i },
    { key: 'revenue', pattern: /Revenue:\s*(\$?[\d,.]+[BMK]?M?)/i },
    { key: 'valuation', pattern: /Valuation:\s*(\$?[\d,.]+[BMK]?M?)/i },
    { key: 'status', pattern: /(?<!Financing )Status:\s*([^A-Z][^:]*?)(?=\s+[A-Z][a-z]+:|$)/i },
    { key: 'financingStatus', pattern: /Financing Status:\s*([^A-Z][^:]*?)(?=\s+[A-Z][a-z]+:|$)/i },
    { key: 'founded', pattern: /Founded:\s*([\d.]+)/i },
    { key: 'employees', pattern: /Employees:\s*([\d,.]+)/i },
    { key: 'activeInvestors', pattern: /Active Investors:\s*([^A-Z][^:]*?)(?=\s+[A-Z][a-z]+:|$)/i },
  ];

  // Extract each field
  fieldPatterns.forEach(({ key, pattern }) => {
    const match = text.match(pattern);
    if (match && match[1]) {
      fields[key] = match[1].trim();
    }
  });

  // Extract Verticals (comma-separated list)
  const verticalsMatch = text.match(/Verticals:\s*([^:]+?)(?=\s+Keywords:|$)/i);
  const verticals = verticalsMatch && verticalsMatch[1]
    ? verticalsMatch[1].split(',').map(v => v.trim()).filter(v => v && v.length > 0 && !v.includes(':'))
    : [];

  // Extract Keywords (comma-separated list at the end)
  const keywordsMatch = text.match(/Keywords:\s*(.+)$/i);
  const keywords = keywordsMatch && keywordsMatch[1]
    ? keywordsMatch[1].split(',').map(k => k.trim()).filter(k => k && k.length > 0)
    : [];

  // Extract clean description (everything before "Industry:" or first field marker)
  const firstFieldIndex = text.search(/\s+(Industry|City|State|Country|Location|Total Raised|Last Financing|Revenue|Status|Financing Status|Founded|Employees|Verticals|Keywords):/i);
  const cleanDescription = firstFieldIndex > 0
    ? text.substring(0, firstFieldIndex).trim()
    : text;

  return { cleanDescription, fields, keywords, verticals };
};
